import { InjectionToken } from '@angular/core';

import type { ProcessingEngine } from './engine.model';

/**
 * How a processing engine gets into the pipeline without jit-ui naming it.
 *
 * WHY THIS EXISTS
 * The engines used to be constructed in `ProcessingPipelineModule`'s
 * constructor, nine literal `registry.register(new …Engine())` calls. That is
 * fine while every engine is open, and it is exactly wrong once jit-ui is
 * public: the YOLO, retinal-layer and stain operations are JAX-only, so their
 * *code* — the operation descriptors, the parameter schemas, the pre/post
 * processing — cannot sit in a public repository, and a build without them must
 * not list them among the available operations.
 *
 * So engines arrive through a multi-provider instead, the same shape the
 * visualization library uses for `TOOLBAR_TOOLS`:
 *
 *     { provide: PROCESSING_ENGINES, multi: true, useFactory: …, deps: […] }
 *
 * Contribute nothing and the pipeline has that many fewer operations; the
 * dialog's operation list, the category grouping and `getAllOperations()` all
 * derive from what is registered, so nothing else has to know.
 *
 * WHAT THIS IS NOT, YET
 * The JAX engines are contributed by `jax-engines.provider.jax.ts`, which
 * `fileReplacements` swaps in for the named build configurations — so a default
 * build neither registers nor bundles them. That keeps them out of the
 * *artifact*, which is not the same as keeping them out of the *repository*:
 * the sources still live here. Moving them into
 * `@jds/jit-extensions` alongside the toolbar tools is the next
 * step, and it needs this contract plus `OperationDescriptor`,
 * `ProcessingImage` and a region-store port published somewhere the plugin can
 * import them. Until then this token is the seam that makes that move a
 * relocation rather than a redesign.
 *
 * WHY A TOKEN AND NOT A `register()` CALL
 * `EngineRegistryService.register()` still exists and still works — the token is
 * read once and forwarded to it. The difference is who decides the set: DI, which
 * a build configuration can vary, rather than a constructor body, which it
 * cannot.
 */
export const PROCESSING_ENGINES = new InjectionToken<readonly ProcessingEngine[]>(
  'PROCESSING_ENGINES',
);
